import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, Loader2, Eye, EyeOff, MailCheck } from "lucide-react";
import { Toaster, toast } from "@/components/ui/sonner";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/mockData";

const inputCls =
  "w-full rounded-lg bg-[#0A0A0B] border border-[#272A30] focus:border-zinc-500 focus:ring-1 focus:ring-zinc-500 outline-none px-3 py-2.5 text-sm placeholder:text-zinc-600 transition-colors";
const labelCls = "block text-[11px] uppercase tracking-[0.18em] text-zinc-500";

export default function Signup() {
  const { signUp, loading } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [needsConfirm, setNeedsConfirm] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Use at least 8 characters for your password.");
      return;
    }
    try {
      const data = await signUp(email.trim().toLowerCase(), password, name.trim());
      if (data.session) {
        toast.success("Account created", { description: "Welcome to Yoobikwitus." });
        navigate("/account", { replace: true });
      } else {
        setNeedsConfirm(true);
      }
    } catch (err) {
      setError(err.message || "Could not create your account");
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#0A0A0B] text-zinc-100 grid lg:grid-cols-2">
      <div className="relative hidden lg:flex flex-col justify-between p-12 border-r border-[#272A30] overflow-hidden">
        <div className="absolute inset-0 grid-noise opacity-60 pointer-events-none" />
        <div className="relative flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-white/5 ring-1 ring-white/10 flex items-center justify-center overflow-hidden">
            <img src={LOGO_URL} alt="Yoobikwitus" className="h-7 w-7 object-contain" />
          </div>
          <div>
            <div className="text-sm font-semibold tracking-tight">Yoobikwitus</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">yoobikwitus.com</div>
          </div>
        </div>

        <div className="relative max-w-md">
          <div className="text-xs uppercase tracking-[0.22em] text-zinc-500 mb-4">Plans</div>
          <h1 className="text-4xl font-semibold tracking-tight leading-[1.05]">
            Start free.
            <br />
            Upgrade when the desk gets busy.
          </h1>
          <div className="mt-8 space-y-3 text-sm">
            <PlanRow name="Free" price="£0 forever" detail="1 image check per day" />
            <PlanRow name="Plus" price="£6.99 / mo" detail="Unlimited checks" accent="text-sky-300" />
            <PlanRow name="Pro" price="£29 / mo" detail="Unlimited checks, batch upload, priority processing" accent="text-emerald-300" />
          </div>
        </div>

        <div className="relative text-[11px] uppercase tracking-[0.18em] text-zinc-600">
          © Yoobikwitus · Editorial verification
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        {needsConfirm ? (
          <div className="w-full max-w-md space-y-5 text-center" data-testid="signup-confirm">
            <MailCheck className="h-10 w-10 mx-auto text-emerald-400" />
            <h2 className="text-2xl font-semibold tracking-tight">Check your inbox</h2>
            <p className="text-sm text-zinc-400">
              We sent a confirmation link to <span className="text-zinc-200">{email}</span>. Click it, then sign in.
            </p>
            <Link
              to="/login"
              className="inline-flex items-center justify-center rounded-lg bg-white text-[#0A0A0B] font-semibold text-sm px-4 py-2.5 hover:bg-zinc-200"
            >
              Go to sign in
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="w-full max-w-md space-y-6" data-testid="signup-form">
            <div>
              <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight">Create your account</h2>
              <p className="mt-2 text-sm text-zinc-400">Free tier, no card required. One check a day.</p>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="name" className={labelCls}>Name (optional)</label>
                <input id="name" data-testid="signup-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Picture editor" className={inputCls} />
              </div>
              <div className="space-y-2">
                <label htmlFor="email" className={labelCls}>Email</label>
                <input id="email" data-testid="signup-email-input" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@newsroom.com" className={inputCls} />
              </div>
              <div className="space-y-2">
                <label htmlFor="password" className={labelCls}>Password</label>
                <div className="relative">
                  <input id="password" data-testid="signup-password-input" type={showPw ? "text" : "password"} autoComplete="new-password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" className={`${inputCls} pr-10`} />
                  <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute inset-y-0 right-2 flex items-center text-zinc-500 hover:text-zinc-300" aria-label={showPw ? "Hide password" : "Show password"}>
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </div>

            {error && (
              <div role="alert" data-testid="signup-error" className="rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 text-sm px-3 py-2">
                {error}
              </div>
            )}

            <button type="submit" disabled={loading} data-testid="signup-submit-button" className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-white text-[#0A0A0B] font-semibold text-sm px-4 py-2.5 hover:bg-zinc-200 disabled:opacity-60 disabled:cursor-not-allowed transition-colors">
              {loading ? (<><Loader2 className="h-4 w-4 animate-spin" /> Creating account…</>) : (<><ShieldCheck className="h-4 w-4" /> Create free account</>)}
            </button>

            <p className="text-sm text-zinc-400 text-center">
              Already have an account?{" "}
              <Link to="/login" className="text-white underline underline-offset-4 hover:text-zinc-200">Sign in</Link>
            </p>
          </form>
        )}
        <Toaster theme="dark" position="bottom-right" />
      </div>
    </div>
  );
}

function PlanRow({ name, price, detail, accent = "text-zinc-200" }) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-[#272A30] bg-[#111318] px-4 py-3">
      <div>
        <div className={`font-semibold ${accent}`}>{name}</div>
        <div className="text-xs text-zinc-500">{detail}</div>
      </div>
      <div className="mono text-zinc-300">{price}</div>
    </div>
  );
}
